/**
 * Node colors synced with theme (index.css).
 * Hex values match: primary, success, destructive, warning, info, muted-foreground.
 * Use these for charts, canvas, and any JS that needs theme hex.
 */
export const NODE_COLORS = {
  primary: '#4a7b9d', // --primary 204 28% 45%
  success: '#2d8f6f', // --success 150 28% 43%
  destructive: '#c44d4d', // --destructive 0 34% 54%
  warning: '#c9a227', // --warning 42 50% 54%
  info: '#4a8fc4', // --info 212 29% 50%
  muted: '#6b7280', // --muted-foreground 207 10% 52%
  purple: '#8b5cf6', // delay (no theme token)
  branch: '#14b8a6', // flow control / condition branch
} as const;

/**
 * Get node color based on type
 */
export const getNodeColor = (type: string, color?: string): string => {
  if (color) return color;

  switch (type) {
    case 'trigger':
      return NODE_COLORS.primary;
    case 'process':
    case 'action':
      return NODE_COLORS.success;
    case 'exit':
    case 'email':
      return NODE_COLORS.destructive;
    case 'condition':
      return NODE_COLORS.warning;
    case 'delay':
    case 'time-delay':
      return NODE_COLORS.purple;
    case 'true-false':
    case 'multi-split':
      return NODE_COLORS.branch;
    case 'webhook':
      return NODE_COLORS.info;
    default:
      return NODE_COLORS.muted;
  }
};

/**
 * Get node icon based on type
 */
export const getNodeIcon = (type: string, icon?: string): string => {
  if (icon) return icon;

  switch (type) {
    case 'trigger':
      return '⚡';
    case 'process':
      return '⚙️';
    case 'exit':
      return '🚪';
    case 'action':
      return '⚙️';
    case 'condition':
      return '❓';
    case 'delay':
      return '⏱️';
    case 'email':
      return '📧';
    case 'webhook':
      return '🔗';
    default:
      return '📋';
  }
};

/**
 * Format delay information for display
 */
export const formatDelayForNode = (delay?: {
  unit: string;
  value: number;
}): string => {
  if (!delay || !delay.value) return '';

  const unit = delay.unit || 'minutes';
  const value = delay.value;

  const unitLabel = value === 1 ? unit.slice(0, -1) : unit;

  return `Wait ${value} ${unitLabel}`;
};

/**
 * Get sending behavior label for display
 */
export const getSendingBehaviorLabel = (
  value: string | null | undefined
): string => {
  if (!value) return '';
  const labels: { [key: string]: string } = {
    send_automatically: 'Send Automatically',
    queue_draft: 'Queue Draft',
    do_not_send: "Don't Send",
  };
  return labels[value] || value;
};

/**
 * Get subscription preference label for display
 */
export const getSubscriptionPreferenceLabel = (
  value: string | null | undefined,
  unsubscribeGroups?: Array<{ _id?: string; title: string }>
): string => {
  if (!value) return '';

  if (unsubscribeGroups) {
    const unsubscribeGroup = unsubscribeGroups.find(
      (group) => group._id === value
    );
    if (unsubscribeGroup) {
      return unsubscribeGroup.title;
    }
  }

  const labels: { [key: string]: string } = {
    use_campaign_settings: 'Use campaign settings',
    subscribed: 'All subscribed people',
    all: 'All subscribed and unsubscribed',
  };
  return labels[value] || value;
};

/**
 * Get message limit label for display
 */
export const getMessageLimitLabel = (
  value: string | null | undefined
): string => {
  if (!value) return '';
  const labels: { [key: string]: string } = {
    use_campaign_settings: 'Use campaign settings',
    do_not_count_toward_limit: "Don't count toward limit",
    count_toward_limit: 'Count toward limit',
  };
  return labels[value] || value;
};

export const estimateNodeHeight = (node: any): number => {
  if (node.measured?.height && node.measured.height > 0) {
    return node.measured.height + 5;
  }

  const nodeType = node.data?.type || node.type || 'default';
  const config = node.data?.config || {};
  const baseHeight = 72;

  switch (nodeType) {
    case 'email': {
      let height = baseHeight + 140;
      if (
        config.sendingBehavior ||
        config.subscriptionPreference ||
        config.messageLimit
      ) {
        const settingsCount = [
          config.sendingBehavior,
          config.subscriptionPreference,
          config.messageLimit,
        ].filter(Boolean).length;
        height += 40 + settingsCount * 20;
      }
      return height;
    }

    case 'time-delay': {
      return baseHeight + 24;
    }

    case 'trigger': {
      const conditions = config.conditions || {};
      const hasConditions =
        Object.keys(conditions).length > 0 &&
        (conditions.and?.length > 0 || conditions.or?.length > 0);
      return baseHeight + (hasConditions ? 40 : 32);
    }

    case 'true-false': {
      return baseHeight + 40;
    }

    case 'webhook': {
      return baseHeight + 52;
    }

    case 'merge': {
      return 8;
    }

    default: {
      const hasConditions =
        config.conditions && Object.keys(config.conditions).length > 0;
      return baseHeight + (hasConditions ? 40 : 24);
    }
  }
};
